/**
 * Error codes the API returns. Mirrors
 * `api/src/common/errors/error-codes.ts`.
 *
 * Duplicated rather than shared: the projects are deliberately independent
 * (ADR 0001), and these strings are API surface the server promises not to
 * repurpose, so a copy is safe. Add codes here as the API grows them.
 */
export const ApiErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  INVALID_GOOGLE_TOKEN: 'INVALID_GOOGLE_TOKEN',
  DOMAIN_NOT_ALLOWED: 'DOMAIN_NOT_ALLOWED',
  NOT_AUTHORIZED_FOR_ADMIN: 'NOT_AUTHORIZED_FOR_ADMIN',
  SESSION_INVALID: 'SESSION_INVALID',
  SSO_NOT_CONFIGURED: 'SSO_NOT_CONFIGURED',

  LLM_NOT_CONFIGURED: 'LLM_NOT_CONFIGURED',
  LLM_UNAVAILABLE: 'LLM_UNAVAILABLE',
  LLM_INVALID_RESPONSE: 'LLM_INVALID_RESPONSE',

  SPEECH_NOT_CONFIGURED: 'SPEECH_NOT_CONFIGURED',
  SPEECH_UNAVAILABLE: 'SPEECH_UNAVAILABLE',
} as const;

export type ApiErrorCode = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export class ApiError extends Error {
  // Written as explicit fields rather than constructor parameter properties:
  // tsconfig sets `erasableSyntaxOnly`, so only syntax that vanishes on type
  // erasure is allowed, and parameter properties emit real code.
  readonly status: number;
  readonly code: string;
  readonly details?: string[];
  readonly requestId?: string;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: string[],
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  is(code: ApiErrorCode): boolean {
    return this.code === code;
  }
}

/** Thrown when the API cannot be reached at all — offline, DNS, CORS. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the Fluently API.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}
